import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Col, DatePicker, Input, Modal, Row, Select, Spin, Table } from "antd";
import { useFormik } from "formik";
import { cloneDeep, get, isEmpty, toLower } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/functional_components/Button/PFButton";
import { setLoading } from "../../redux/ducks/Loading";
import FuelCard from "../../services/api/FuelCard";
import { dbDate, langDate, paginationDefaults } from "../../utils/properties";
import { buildQueryString, exportFile, showError, showSuccess, trimData } from "../../utils/utilities";
import Filter from "../../components/functional_components/Filter/Filter";
import { setFilters } from "../../redux/ducks/Filters";
import ParkAuto from "../../services/api/ParkAuto";
import User from "../../services/api/User";
import CarUser from "../../services/api/CarUser";
import dayjs from "dayjs";
const { Option } = Select;

const FuelCards = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { confirm } = Modal;
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langDate[toLower(currentLanguage)];
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    parkAutoList: [],
    hrList: [],
    hrSearchList: [],
    loading: false,
    downloadLoading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString(filters.obj, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  }, []);

  const formikFuelCard = useFormik({
    initialValues: {
      periodStart:
        get(state.modal.data, "periodStart", null) !== null ? dayjs(state.modal.data.periodStart, dbDate) : null,
      periodEnd: get(state.modal.data, "periodEnd", null) !== null ? dayjs(state.modal.data.periodEnd, dbDate) : null,
      cardNumber: get(state.modal.data, "cardNumber", null),
      parkAuto: get(state.modal.data, "parkAuto.id", null),
      user: get(state.modal.data, "user.username", null),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.periodStart) {
        errors.periodStart = "Required!";
      }
      if (!values.periodEnd) {
        errors.periodEnd = "Required!";
      }
      if (!values.cardNumber) {
        errors.cardNumber = "Required!";
      }
      if (!values.parkAuto) {
        errors.parkAuto = "Required!";
      }
      if (!values.user) {
        errors.user = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const data = {
        ...values,
        periodStart: dayjs(values.periodStart, dateFormat).format(dbDate),
        periodEnd: dayjs(values.periodEnd, dateFormat).format(dbDate),
        parkAuto: state.parkAutoList.find((c) => c.id === values.parkAuto),
        user: state.hrList.find((h) => h.username === values.user),
        id: state.modal.data?.id || undefined,
      };
      const saveResponse = await FuelCard.setFuelCard(data);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        const queryString = buildQueryString(filters.obj, {});
        dispatch(setFilters(queryString));
        dataList(queryString.string);
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const dataList = async (queryString) => {
    dispatch(setLoading(true));
    const { data, error, currentPage, pageSize, totalItems } = await FuelCard.getFuelCards(queryString);
    if (!error && data) {
      data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: data || [],
        pagination: {
          currentPage: currentPage ? currentPage + 1 : paginationDefaults.page,
          size: pageSize || paginationDefaults.size,
          totalItems: totalItems || paginationDefaults.totalItems,
        },
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const closeModal = () => {
    formikFuelCard.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const { data, error } = await FuelCard.getFuelCard(id);
    if (!error && data) {
      setState((prevState) => ({
        ...prevState,
        parkAutoList: [data.parkAuto],
        hrList: [data.user],
        modal: { visible: true, type: "edit", data },
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const onSearchParkAutos = async (value) => {
    if (value.length >= 2) {
      const { error, data } = await ParkAuto.getParkAutos(`allPages=true&plate=${value}`);
      if (!error && data) {
        setState((prevState) => ({
          ...prevState,
          parkAutoList: data,
        }));
      }
    } else {
      setState((prevState) => ({ ...prevState, parkAutoList: [] }));
    }
  };

  const onSearchusers = async (value, filter = false) => {
    if (value.length >= 2) {
      const { error, data } = await User.getUsersInternalHrList(`allPages=true&surname=${value}`);
      if (!error && data) {
        if (filter) {
          setState((prevState) => ({
            ...prevState,
            hrSearchList: data.map((u) => ({ value: u.username, label: `${u.name} ${u.surname}` })),
          }));
        } else {
          setState((prevState) => ({
            ...prevState,
            hrList: data,
          }));
        }
      }
    } else {
      setState((prevState) => ({ ...prevState, hrList: [], hrSearchList: [] }));
    }
  };

  const deleteCallback = (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await FuelCard.deleteFuelCard(id);
        if (!deleteResponse.error) {
          showSuccess();
          const queryString = buildQueryString(filters.obj, {});
          dispatch(setFilters(queryString));
          dataList(queryString.string);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const columns = [
    {
      title: t("fuelCard.table.periodStart"),
      dataIndex: "periodStart",
      key: "periodStart",
      render: (periodStart) => dayjs(periodStart, dbDate).format(langDate[toLower(currentLanguage)]),
    },
    {
      title: t("fuelCard.table.periodEnd"),
      dataIndex: "periodEnd",
      key: "periodEnd",
      render: (periodEnd) => dayjs(periodEnd, dbDate).format(langDate[toLower(currentLanguage)]),
    },
    {
      title: t("fuelCard.table.user"),
      dataIndex: "user",
      key: "user",
      render: (_, data) => `${data.user.name} ${data.user.surname}`,
    },
    {
      title: t("fuelCard.table.plate"),
      dataIndex: "plate",
      key: "plate",
      render: (_, data) => data.parkAuto.plate,
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => {
        return (
          <div className="space-around">
            <div onClick={() => editCallback(data.id)}>
              <EditOutlined className="icon-medium" />
            </div>
            <div onClick={() => deleteCallback(data.id)}>
              <DeleteOutlined className="icon-medium" />
            </div>
          </div>
        );
      },
    },
  ];

  const formikFilter = useFormik({
    initialValues: {
      periodStart: "",
      periodEnd: "",
      user: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      // When I search, I start my page to 0
      let dataFilter = cloneDeep(values);
      dataFilter["page"] = 0;
      if (values.periodStart) {
        dataFilter.periodStart =
          dataFilter.periodStart !== "" ? dayjs(dataFilter.periodStart, dateFormat).format(dbDate) : undefined;
      } else {
        delete dataFilter.periodStart;
      }
      if (values.periodEnd) {
        dataFilter.periodEnd =
          dataFilter.periodEnd !== "" ? dayjs(dataFilter.periodEnd, dateFormat).format(dbDate) : undefined;
      } else {
        delete dataFilter.periodEnd;
      }
      dataFilter["user.username"] = values.user;
      delete dataFilter.user;
      const queryString = buildQueryString(filters.obj, dataFilter);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "date",
      allowClear: true,
      isRequired: false,
      id: "periodStart",
      name: "periodStart",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("fuelCard.table.periodStart"),
      value: formikFilter.values.periodStart,
      onChange: (date) => {
        formikFilter.setFieldValue("periodStart", date);
      },
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "date",
      isRequired: false,
      id: "periodEnd",
      name: "periodEnd",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("fuelCard.table.periodEnd"),
      value: formikFilter.values.periodEnd,
      onChange: (date) => {
        formikFilter.setFieldValue("periodEnd", date);
      },
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "selectWithSearch",
      isRequired: false,
      id: "user",
      name: "user",
      value: formikFilter.values.user,
      label: t("creditCard.table.user") + " " + t("creditCard.userLabelInfo"),
      onChange: formikFilter.handleChange("user"),
      onBlur: formikFilter.handleBlur("user"),
      onSearch: (e) => onSearchusers(e, true),
      dataSelect: state.hrSearchList,
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString({ page: paginationDefaults.page - 1, size: paginationDefaults.size }, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  const getCarUser = async (field, value) => {
    if (value) {
      let queryString = `date=${dayjs(formikFuelCard.values.periodStart, dateFormat).format(dbDate)}&`;
      if (field === "user") {
        queryString = queryString + `user.username=${value}`;
        formikFuelCard.setFieldValue("parkAuto", null);
        setState((prevState) => ({ ...prevState, parkAutoList: [] }));
      } else if (field === "parkAuto") {
        queryString = queryString + `parkAuto.id=${value}`;
        formikFuelCard.setFieldValue("user", null);
        setState((prevState) => ({ ...prevState, hrList: [] }));
      }
      const { data, error } = await CarUser.getCarUsers(queryString);
      if (!error && data) {
        if (!isEmpty(data)) {
          if (field === "user") {
            formikFuelCard.setFieldValue("parkAuto", data[0].parkAuto.id);
            setState((prevState) => ({ ...prevState, parkAutoList: [data[0].parkAuto] }));
          } else if (field === "parkAuto") {
            formikFuelCard.setFieldValue("user", data[0].user.username);
            setState((prevState) => ({ ...prevState, hrList: [data[0].user] }));
          }
        }
      } else {
        showError(get(error, "messageCode", "ER000"));
      }
    } else {
      formikFuelCard.setFieldValue("user", null);
      formikFuelCard.setFieldValue("parkAuto", null);
    }
  };

  const downloadExport = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const exportResponse = await FuelCard.downloadExportFile();
    if (!exportResponse.error) {
      const blob = new Blob([exportResponse]);
      exportFile(blob, "Fuel_Card.csv");
      showSuccess();
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} allInOneRow />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
        <Button
          className="ml-10"
          isLoading={state.downloadLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={() => downloadExport()}
        />
      </Row>
      <Table
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(queryString.string);
          },
        }}
      />
      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.brand
            ? state.modal.data.brand
            : t("fuelCard.new")
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikFuelCard.submitForm}
            isDisabled={!(formikFuelCard.dirty && formikFuelCard.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]}>
          <Col span={24}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="cardNumber">
                {t("fuelCard.table.cardNumber")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="cardNumber"
                name="cardNumber"
                onChange={formikFuelCard.handleChange}
                onBlur={formikFuelCard.handleBlur}
                value={formikFuelCard.values.cardNumber}
                maxLength={20}
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="periodStart">
                {t("fuelCard.table.periodStart")}*
              </label>
            </Row>
            <Row>
              <DatePicker
                className="w-100"
                allowClear={false}
                id="periodStart"
                name="periodStart"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                onChange={(date) => {
                  formikFuelCard.setFieldValue("periodStart", date);
                }}
                onBlur={formikFuelCard.handleBlur}
                value={formikFuelCard.values.periodStart}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="periodEnd">
                {t("fuelCard.table.periodEnd")}*
              </label>
            </Row>
            <Row>
              <DatePicker
                className="w-100"
                allowClear={false}
                id="periodEnd"
                name="periodEnd"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                onChange={(date) => {
                  formikFuelCard.setFieldValue("periodEnd", date);
                }}
                onBlur={formikFuelCard.handleBlur}
                value={formikFuelCard.values.periodEnd}
                disabledDate={(current) => current && current < dayjs(formikFuelCard.values.periodStart)}
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="parkAuto">
                {t("fuelCard.table.parkAuto")}*
              </label>
            </Row>
            <Row>
              <p>{t("fuelCard.parkAutoLabelInfo")}</p>
            </Row>
            <Row>
              <Select
                allowClear
                onClear={() => {
                  setState((prevState) => ({ ...prevState, parkAutoList: [] }));
                }}
                className="w-100"
                id="parkAuto"
                optionFilterProp="value"
                name="parkAuto"
                showSearch
                placeholder={t("fuelCard.table.parkAuto")}
                onChange={(value) => {
                  getCarUser("parkAuto", value);
                  formikFuelCard.setFieldValue("parkAuto", value);
                }}
                onBlur={() => {
                  formikFuelCard.handleBlur("parkAuto");
                }}
                onSearch={(value) => onSearchParkAutos(value)}
                filterOption={(input, option) => option.name.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                value={formikFuelCard.values.parkAuto}
                disabled={!formikFuelCard.values.periodStart}
              >
                {state.parkAutoList.length > 0 &&
                  state.parkAutoList.map((parkAuto, idx) => {
                    return (
                      <Option key={idx} value={parkAuto.id} name={parkAuto.plate}>
                        {parkAuto.plate} - {parkAuto.brand} | {parkAuto.model}
                      </Option>
                    );
                  })}
              </Select>
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="user">
                {t("fuelCard.table.user")}*
              </label>
            </Row>
            <Row>
              <p>{t("fuelCard.userLabelInfo")}</p>
            </Row>
            <Row>
              <Select
                allowClear
                onClear={() => {
                  setState((prevState) => ({ ...prevState, hrList: [] }));
                }}
                className="w-100"
                id="user"
                optionFilterProp="value"
                name="user"
                showSearch
                placeholder={t("fuelCard.table.user")}
                onChange={(value) => {
                  getCarUser("user", value);
                  formikFuelCard.setFieldValue("user", value);
                }}
                onBlur={() => {
                  formikFuelCard.handleBlur("user");
                }}
                onSearch={(value) => onSearchusers(value)}
                filterOption={(input, option) => option.name.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                value={formikFuelCard.values.user}
                disabled={!formikFuelCard.values.periodStart}
              >
                {state.hrList.length > 0 &&
                  state.hrList.map((user, idx) => {
                    return (
                      <Option key={idx} value={user.username} name={user.surname}>
                        {user.name} {user.surname}
                      </Option>
                    );
                  })}
              </Select>
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default FuelCards;
